import React from 'react';
import { Globe, Heart, Newspaper, Flame, ExternalLink, Sparkles } from 'lucide-react';
import { WorldMatchFixture, FootballNewsItem } from '../types';

interface WorldFootballViewProps {
  fixtures: WorldMatchFixture[];
  news: FootballNewsItem[];
  onToggleFavorite: (id: string) => void;
}

export const WorldFootballView: React.FC<WorldFootballViewProps> = ({
  fixtures,
  news,
  onToggleFavorite
}) => {
  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      
      {/* Top Banner */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] bg-blue-500/20 text-blue-400 font-extrabold uppercase px-2.5 py-0.5 rounded-full border border-blue-500/30">
              FotMob Style Feed
            </span>
            <span className="text-xs text-slate-400">• Separated from Our Turf Matches</span>
          </div>
          <h2 className="text-2xl font-black text-white">World Football Live Scores & News</h2>
          <p className="text-xs text-slate-400">Follow Champions League, Premier League, Barca, Real Madrid & world news</p>
        </div>
      </div>

      {/* World Live Scores */}
      <div className="space-y-3">
        <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-emerald-400" /> Live Scores & Fixtures
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fixtures.map((f) => (
            <div key={f.id} className="bg-slate-900 border border-slate-800 p-4 rounded-3xl space-y-3">
              <div className="flex justify-between items-center border-b border-slate-800/80 pb-2">
                <span className="text-[10px] font-extrabold text-blue-400 uppercase tracking-wider">{f.league}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold font-mono px-2 py-0.5 rounded-full ${
                    f.isLive ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-800 text-slate-400'
                  }`}>
                    {f.timeOrStatus}
                  </span>
                  <button onClick={() => onToggleFavorite(f.id)} className="text-slate-400 hover:text-red-400 transition">
                    <Heart className={`w-4 h-4 ${f.isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Teams & Scores */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img src={f.homeLogo} alt={f.homeTeam} className="w-6 h-6 rounded-full object-cover" />
                    <span className="font-extrabold text-sm text-white">{f.homeTeam}</span>
                  </div>
                  <span className="font-mono font-black text-lg text-emerald-400">{f.homeScore ?? '-'}</span>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img src={f.awayLogo} alt={f.awayTeam} className="w-6 h-6 rounded-full object-cover" />
                    <span className="font-extrabold text-sm text-white">{f.awayTeam}</span>
                  </div>
                  <span className="font-mono font-black text-lg text-emerald-400">{f.awayScore ?? '-'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Football News Feed */}
      <div className="space-y-3 pt-4">
        <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
          <Newspaper className="w-4 h-4 text-amber-400" /> Latest Football Headlines
        </h3>

        <div className="space-y-3">
          {news.map((item) => (
            <div key={item.id} className="bg-slate-900 border border-slate-800 p-4 rounded-3xl flex flex-col sm:flex-row gap-4 items-center">
              <img src={item.imageUrl} alt={item.title} className="w-full sm:w-36 h-28 rounded-2xl object-cover shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="flex justify-between items-center text-[10px] font-mono text-slate-400">
                  <span className="text-amber-400 font-bold">{item.category}</span>
                  <span>{item.publishedTime} • {item.source}</span>
                </div>
                <h4 className="font-extrabold text-sm text-white leading-snug">{item.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{item.summary}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};
